import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import { Button } from '@winnin/winnin_ui.components.button'
import { Title } from '@winnin/winnin_ui.components.title'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { ReactSelect as Select, SelectAgesButtons, SelectGendersButtons } from 'components'
import BlockComponentsRole75Checker from 'components/BlockComponentsRole75Checker'
import { COLORS } from 'consts'
import { T, useTranslate } from 'lib/tolgee'
import { analyticsTracker, Filters, localStorageUtils } from 'utils'
import TOLGEE_GLOBALS from 'utils/tolgeeGlobals'

import QuestionMarkTooltip from '../../QuestionMarkTooltip'
import { SAVE_FILTER, SAVE_FILTER_LOCALSTORAGE } from '../query'

function AudienceFilterContent({ filters, isCountryDisabled, onTrack, changeAudienceFilter, onApply, onCancel, data }) {
  const t = useTranslate()

  const [saveFilter, { loading: loadingFilter }] = useMutation(SAVE_FILTER, {
    onCompleted() {
      createToast({
        content: t({
          key: 'AudienceFilterContent-toastSuccessMessage',
          defaultValue: 'Audience saved successfully!',
        }),
      })
    },
    onError(error) {
      console.error('SAVE FILTER @ SAVE_FILTER', error)

      createToast({
        content: t({
          key: 'AudienceFilterContent-toastErrorMessage',
          defaultValue: 'Error saving audience, try again!',
        }),
      })
    },
  })

  const [saveFilterBatch] = useMutation(SAVE_FILTER_LOCALSTORAGE, {
    onCompleted() {
      localStorageUtils.delete('audienceFilters')
    },
    onError(error) {
      console.error('SAVE FILTER @ SAVE_FILTER_LOCALSTORAGE', error)
    },
    refetchQueries: ['getWorkspaceSavedFilters'],
  })

  const [audienceAges, setAudienceAges] = useState(filters?.audienceAges)
  const [audienceGenders, setAudienceGenders] = useState(filters?.audienceGenders)
  const [audienceCountries, setAudienceCountries] = useState(filters?.audienceCountries)
  const { createToast } = useToast()

  useEffect(() => {
    const savedFiltersLocalStorage = localStorageUtils.getObject('audienceFilters') || []
    if (savedFiltersLocalStorage.length > 0) {
      const filterValues = savedFiltersLocalStorage.map(item => {
        return {
          age: Array.isArray(item.value.audienceAges) ? item.value.audienceAges.map(age => age.value) : [],
          gender: item.value.audienceGenders.value ? item.value.audienceGenders.value : 'all',
          country: item.value.audienceCountries.value ? item.value.audienceCountries.value : 'all',
        }
      })
      saveFilterBatch({
        variables: {
          filter: filterValues,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOnApply = () => {
    changeAudienceFilter?.('audienceAges', audienceAges)
    changeAudienceFilter?.('audienceGenders', audienceGenders)
    changeAudienceFilter?.('audienceCountries', audienceCountries)
    const variables = {
      audienceAges,
      audienceGenders,
      audienceCountries,
    }
    onApply(variables)
  }

  const handleSave = () => {
    const formattedAgeValues = Array.isArray(audienceAges)
      ? audienceAges?.map(item => item.value)
      : [audienceAges?.value]
    const formattedGenderValue = audienceGenders?.value ? audienceGenders?.value : 'all'
    const formattedCountryValue = audienceCountries?.value ? audienceCountries?.value : 'all'

    const filter = {
      age: formattedAgeValues[0] === '' ? [] : formattedAgeValues,
      gender: formattedGenderValue,
      country: formattedCountryValue,
    }

    const currentFilters = {
      audienceAges,
      audienceGenders,
      audienceCountries,
    }

    const isAudienceSaved = data?.getWorkspaceSavedFilters.some(({ filter: item }) => {
      const { age, country, gender } = item
      return JSON.stringify({ age, gender, country }) === JSON.stringify(filter)
    })

    if (isAudienceSaved) {
      createToast({
        content: t({
          key: 'AudienceFilterContent-toastSavedMessage',
          defaultValue: 'This audience is already saved',
        }),
      })
    } else {
      saveFilter({
        variables: {
          filter,
        },
      })
    }

    const { TARGET_AUDIENCE_SAVE } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(TARGET_AUDIENCE_SAVE, {
      filterValues: currentFilters,
    })
  }

  return (
    <>
      <Title tag="h5" className="AudienceDropdownContent-title">
        <T keyName="AudienceFilterContent-header">Estimated Audience</T>
        <QuestionMarkTooltip
          position={'tooltipped-s'}
          title={t({
            key: 'AudienceFilterContent-tooltip',
            defaultValue:
              'Audience demographics is estimated by our proprietary artificial intelligence tool using public data from people engaging with this content.',
          })}
        />
      </Title>
      <div className="audience-options">
        <div className="audience-options-item">
          <SelectGendersButtons audienceFilterProp filter={audienceGenders} setFilter={setAudienceGenders} />
        </div>
        <div className="audience-options-item">
          <SelectAgesButtons
            containerClass={'buttons-grid-container'}
            audienceFilterProp
            filter={audienceAges}
            setFilter={setAudienceAges}
          />
        </div>
        <div className="audience-options-item">
          <Title weight="regular" tag="h4" className="audience-options-item-label">
            <T keyName={TOLGEE_GLOBALS.country.key}>{TOLGEE_GLOBALS.country.defaultValue}</T>
          </Title>
          <div
            aria-label={t({
              key: 'AudienceFilterContent-countryDisabledTooltip',
              defaultValue: 'Country filter is not applicable at this time',
            })}
            className={classNames({
              'tooltipped tooltipped-s tooltipped-multiline': isCountryDisabled,
            })}
          >
            <Select
              className="audience-country-select"
              placeholder="Select an option"
              name="audience-country-select"
              onChange={setAudienceCountries}
              customThemeColors={{
                primary25: COLORS.primary_light2,
                primary50: COLORS.primary_light2,
                primary: COLORS.primary,
              }}
              value={audienceCountries}
              options={Filters.get('audienceFilters', 'audienceCountries')}
              isDisabled={isCountryDisabled}
            />
          </div>
        </div>
      </div>
      <div className="audience-actions">
        <BlockComponentsRole75Checker>
          <Button className="start" onClick={handleSave} appearance="outline" disabled={loadingFilter}>
            {!loadingFilter
              ? t({ key: 'AudienceFilterContent-saveAudience', defaultValue: 'Save audience' })
              : t({ key: 'AudienceFilterContent-savingAudience', defaultValue: 'Saving...' })}
          </Button>
        </BlockComponentsRole75Checker>
        <div className="buttonWrapper">
          <Button onClick={onCancel} appearance="link">
            <T keyName={TOLGEE_GLOBALS.cancel.key}>{TOLGEE_GLOBALS.cancel.defaultValue}</T>
          </Button>
          <Button
            onClick={() => {
              handleOnApply()
              onTrack?.(filters)
            }}
          >
            <T keyName={TOLGEE_GLOBALS.apply.key}>{TOLGEE_GLOBALS.apply.defaultValue}</T>
          </Button>
        </div>
      </div>
    </>
  )
}

export default AudienceFilterContent
